import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, orderBy } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '../../../hooks/AuthContext';
import InstructorSearchSelect from '../components/InstructorSearchSelect';
import { getInstructors } from '../services';

const PublicSchedulePage = () => {
    const { instructorId } = useParams();
    const [instructor, setInstructor] = useState(null);
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('tabla'); // tabla | calendario
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const [instructorsList, setInstructorsList] = useState([]);

    useEffect(() => {
        if (!isAdmin) return;
        let active = true;
        (async () => {
            try {
                const list = await getInstructors();
                if (active) setInstructorsList(list);
            } catch (err) {
                console.error('Error cargando instructores:', err);
            }
        })();
        return () => { active = false; };
    }, [isAdmin]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch instructor
                const instDoc = await getDoc(doc(db, 'instructors', instructorId));
                if (!instDoc.exists()) { setLoading(false); return; }
                setInstructor({ id: instDoc.id, ...instDoc.data() });

                // Fetch schedules for this instructor (future only)
                const now = new Date();
                const q = query(
                    collection(db, 'schedules'),
                    where('instructorId', '==', instructorId),
                    orderBy('start', 'asc')
                );
                const snap = await getDocs(q);
                const events = snap.docs.map(d => {
                    const data = d.data();
                    return {
                        id: d.id,
                        ...data,
                        start: data.start?.toDate ? data.start.toDate() : new Date(data.start),
                        end: data.end?.toDate ? data.end.toDate() : new Date(data.end),
                    };
                }).filter(e => e.start >= new Date(now.getFullYear(), now.getMonth(), 1)); // From start of current month

                setSchedules(events);
            } catch (err) {
                console.error('Error loading schedule:', err);
            }
            setLoading(false);
        };
        fetchData();
    }, [instructorId]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!instructor) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-xl font-bold text-gray-800">Instructor no encontrado</p>
                    <p className="text-sm text-gray-500 mt-1">El enlace puede ser incorrecto o el instructor fue eliminado.</p>
                </div>
            </div>
        );
    }

    // Group by course
    const byCourse = {};
    schedules.forEach(s => {
        const key = s.courseId || 'sin-curso';
        if (!byCourse[key]) byCourse[key] = { name: s.title?.replace(/^(MC|Clase)\s+/, '') || 'Sin curso', events: [] };
        byCourse[key].events.push(s);
    });

    // Group by month
    const byMonth = {};
    schedules.forEach(s => {
        const key = format(s.start, 'yyyy-MM');
        if (!byMonth[key]) byMonth[key] = { label: format(s.start, 'MMMM yyyy', { locale: es }), events: [] };
        byMonth[key].events.push(s);
    });

    const totalMC = schedules.filter(s => s.type === 'masterclass').length;
    const totalClases = schedules.filter(s => s.type === 'clase').length;
    const upcoming = schedules.filter(s => s.start > new Date());

    // Availability days
    const availDays = Object.entries(instructor.availability || {})
        .filter(([, v]) => v && v.length > 0)
        .map(([k]) => {
            const names = { monday: 'Lun', tuesday: 'Mar', wednesday: 'Mié', thursday: 'Jue', friday: 'Vie', saturday: 'Sáb', sunday: 'Dom' };
            return names[k] || k;
        });

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
            {isAdmin && (
                <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 sticky top-0 z-10">
                    <div className="max-w-4xl mx-auto flex items-center gap-3">
                        <span className="text-[11px] font-semibold text-amber-700 uppercase">Modo admin:</span>
                        <div className="flex-1 max-w-xs">
                            <InstructorSearchSelect
                                instructors={instructorsList}
                                value={instructorId}
                                onChange={(id) => { if (id && id !== instructorId) navigate(`/horario/${id}`); }}
                                placeholder="Cambiar instructor..."
                            />
                        </div>
                    </div>
                </div>
            )}
            {/* Header */}
            <div className="bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-4xl mx-auto px-6 py-6">
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-bold text-white shadow-lg"
                            style={{ backgroundColor: instructor.color || '#3b82f6' }}>
                            {instructor.name?.charAt(0)?.toUpperCase() || 'I'}
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">{instructor.name}</h1>
                            <p className="text-sm text-gray-500">Programación de clases</p>
                            <div className="flex items-center gap-2 mt-1">
                                {availDays.map(d => (
                                    <span key={d} className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">{d}</span>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4 mt-5">
                        <div className="bg-purple-50 rounded-xl p-3 text-center">
                            <p className="text-2xl font-bold text-purple-700">{totalMC}</p>
                            <p className="text-xs text-purple-600">Masterclasses</p>
                        </div>
                        <div className="bg-blue-50 rounded-xl p-3 text-center">
                            <p className="text-2xl font-bold text-blue-700">{totalClases}</p>
                            <p className="text-xs text-blue-600">Clases</p>
                        </div>
                        <div className="bg-green-50 rounded-xl p-3 text-center">
                            <p className="text-2xl font-bold text-green-700">{upcoming.length}</p>
                            <p className="text-xs text-green-600">Próximas</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* View toggle */}
            <div className="max-w-4xl mx-auto px-6 pt-4">
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5 w-fit">
                    <button onClick={() => setViewMode('tabla')}
                        className={`px-4 py-1.5 text-xs font-medium rounded-md transition ${viewMode === 'tabla' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500'}`}>
                        Tabla
                    </button>
                    <button onClick={() => setViewMode('curso')}
                        className={`px-4 py-1.5 text-xs font-medium rounded-md transition ${viewMode === 'curso' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500'}`}>
                        Por Curso
                    </button>
                    <button onClick={() => setViewMode('mes')}
                        className={`px-4 py-1.5 text-xs font-medium rounded-md transition ${viewMode === 'mes' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500'}`}>
                        Por Mes
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto px-6 py-4 pb-12">
                {schedules.length === 0 ? (
                    <div className="text-center py-16 text-gray-400">
                        <p className="text-lg">Sin eventos programados</p>
                    </div>
                ) : viewMode === 'tabla' ? (
                    /* Table view */
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 text-xs text-gray-500 uppercase">
                                    <th className="px-4 py-3 text-left font-semibold">#</th>
                                    <th className="px-4 py-3 text-left font-semibold">Tipo</th>
                                    <th className="px-4 py-3 text-left font-semibold">Título</th>
                                    <th className="px-4 py-3 text-left font-semibold">Fecha</th>
                                    <th className="px-4 py-3 text-left font-semibold">Horario</th>
                                    <th className="px-4 py-3 text-center font-semibold">Estado</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {schedules.map((s, i) => {
                                    const isPast = s.start < new Date();
                                    return (
                                        <tr key={s.id} className={`transition-colors ${isPast ? 'opacity-50' : 'hover:bg-gray-50'}`}>
                                            <td className="px-4 py-2.5 text-xs text-gray-400">{i + 1}</td>
                                            <td className="px-4 py-2.5">
                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.type === 'masterclass' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                                    {s.type === 'masterclass' ? 'MC' : 'Clase'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2.5 font-medium text-gray-800">{s.title}</td>
                                            <td className="px-4 py-2.5 text-gray-600">
                                                {format(s.start, "EEE d MMM", { locale: es })}
                                            </td>
                                            <td className="px-4 py-2.5 font-mono text-xs text-gray-600">
                                                {format(s.start, 'HH:mm')} - {format(s.end, 'HH:mm')}
                                            </td>
                                            <td className="px-4 py-2.5 text-center">
                                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${isPast ? 'bg-gray-100 text-gray-500' : s.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                                    {isPast ? 'Pasado' : s.status === 'confirmed' ? 'Confirmado' : 'Borrador'}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : viewMode === 'curso' ? (
                    /* By course */
                    <div className="space-y-4">
                        {Object.entries(byCourse).map(([key, group]) => (
                            <div key={key} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                                    <h3 className="text-sm font-bold text-gray-800">{group.name}</h3>
                                    <span className="text-xs text-gray-500">{group.events.length} eventos</span>
                                </div>
                                <div className="divide-y divide-gray-100">
                                    {group.events.map((s, i) => (
                                        <div key={s.id} className="px-4 py-2.5 flex items-center justify-between text-sm">
                                            <div className="flex items-center gap-3">
                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.type === 'masterclass' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                                    {s.type === 'masterclass' ? 'MC' : 'CL'}
                                                </span>
                                                <span className="text-gray-800">{format(s.start, "EEE d MMM", { locale: es })}</span>
                                            </div>
                                            <span className="font-mono text-xs text-gray-500">{format(s.start, 'HH:mm')} - {format(s.end, 'HH:mm')}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    /* By month */
                    <div className="space-y-4">
                        {Object.entries(byMonth).sort().map(([key, group]) => (
                            <div key={key} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                    <h3 className="text-sm font-bold text-gray-800 capitalize">{group.label}</h3>
                                </div>
                                <div className="divide-y divide-gray-100">
                                    {group.events.map(s => (
                                        <div key={s.id} className="px-4 py-2.5 flex items-center justify-between text-sm">
                                            <div className="flex items-center gap-3">
                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.type === 'masterclass' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                                    {s.type === 'masterclass' ? 'MC' : 'CL'}
                                                </span>
                                                <span className="text-gray-800 font-medium">{s.title}</span>
                                            </div>
                                            <div className="text-right">
                                                <span className="text-xs text-gray-600">{format(s.start, "EEE d", { locale: es })}</span>
                                                <span className="font-mono text-xs text-gray-400 ml-2">{format(s.start, 'HH:mm')}-{format(s.end, 'HH:mm')}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="bg-white border-t border-gray-200 py-4 text-center text-xs text-gray-400">
                Generado por GestorPro — Hingenia
            </div>
        </div>
    );
};

export default PublicSchedulePage;
